/**
 * useEventSectionCounts — single source of truth for the six Events-section
 * tab counts (Events · Event Hubs · Event Templates · Tickets · Landing Pages ·
 * Archive). Every event-section screen calls this and passes the result into
 * `eventsSectionTabs`, so each tab shows the SAME total on every screen —
 * counts no longer disappear or change when you switch tabs.
 *
 * Counts read the same data sources the destination screens render from:
 *   events    → active (non-archived) events
 *   hubs      → useHubs()
 *   templates → useTemplates()
 *   tickets   → useTicketTypes()   (the "Tickets" tab routes to ticket-types)
 *   landing   → useLandingPages()
 *   archived  → past (ended) events
 */
import { useEvents } from "./useEvents";
import { useHubs } from "../hubs/hubs-data";
import { useTemplates } from "../templates/useTemplates";
import { useTicketTypes } from "../tickets/ticket-types-store";
import { useLandingPages } from "../landing/LandingPagesIndex";

export interface EventSectionCounts {
  events: number; hubs: number; templates: number;
  tickets: number; landing: number; archived: number;
  // Index signature so the counts object satisfies eventsSectionTabs'
  // Partial<Record<string, number>> parameter.
  [key: string]: number;
}

export function useEventSectionCounts(): EventSectionCounts {
  const { events } = useEvents();
  const hubs = useHubs();
  const { templates } = useTemplates();
  const tickets = useTicketTypes();
  const landing = useLandingPages();
  return {
    events: events.filter((e) => e.status !== "past").length,
    hubs: hubs.length,
    templates: templates.length,
    tickets: tickets.length,
    landing: landing.length,
    archived: events.filter((e) => e.status === "past").length,
  };
}
